import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { usePageTitle } from "@/lib/usePageTitle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PaymentSuccess = () => {
  usePageTitle("Payment Confirmed", "Your session payment has been received.");
  const { search } = useLocation();
  const sessionId = new URLSearchParams(search).get("session_id");
  const [state, setState] = useState("checking");

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries < 12) setTimeout(poll, 2000);
      else setState("pending");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="mx-auto max-w-xl px-5 pb-24 pt-24 text-center sm:px-8" data-testid="payment-success-page">
      <Reveal>
        {state === "checking" && (
          <>
            <Loader2 size={48} className="mx-auto animate-spin text-[#527363]" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-3xl font-medium text-[#1E2723]">Confirming your payment…</h1>
            <p className="mt-3 text-sm text-[#54645B]" data-testid="payment-checking-message">This takes a few seconds.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={52} className="mx-auto text-[#527363]" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-3xl font-medium text-[#1E2723]">Payment received — thank you.</h1>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A5750]" data-testid="payment-success-message">
              Your session payment has been confirmed and Brian has been notified. A receipt from Stripe will arrive in your email shortly.
            </p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 size={48} className="mx-auto text-[#65756C]" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-3xl font-medium text-[#1E2723]">Almost there</h1>
            <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A5750]" data-testid="payment-pending-message">
              Your payment is being processed. If it doesn't confirm within a few minutes, it will still be recorded — Brian can see it on his side.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-[#B85C43]" strokeWidth={1.5} />
            <h1 className="mt-6 font-serif text-3xl font-medium text-[#1E2723]">Something looks off</h1>
            <p className="mt-3 text-sm text-[#54645B]">No payment reference found. If you completed a payment, it has still been recorded.</p>
          </>
        )}
        <Link to="/" data-testid="payment-home-link" className="btn-secondary mt-8">
          Back to home <ArrowRight size={15} />
        </Link>
      </Reveal>
    </div>
  );
};

export const PaymentCancel = () => {
  usePageTitle("Payment Cancelled", "Your payment was cancelled — nothing was charged.");
  return (
    <div className="mx-auto max-w-xl px-5 pb-24 pt-24 text-center sm:px-8" data-testid="payment-cancel-page">
      <Reveal>
        <XCircle size={48} className="mx-auto text-[#B85C43]" strokeWidth={1.5} />
        <h1 className="mt-6 font-serif text-3xl font-medium text-[#1E2723]">Payment cancelled</h1>
        <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A5750]" data-testid="payment-cancel-message">
          Nothing was charged. You can return to the payment page whenever you're ready — no pressure.
        </p>
        <Link to="/pay" data-testid="payment-retry-link" className="btn-primary mt-8">
          Return to payment <ArrowRight size={15} />
        </Link>
      </Reveal>
    </div>
  );
};
